import { createClient } from '@/utils/supabase/client';
import { create } from 'zustand';

export interface Festival {
    id: number;
    name: string;
    year: number;
}

export interface User {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    pronouns?: string;
    phone?: string;
    companyName?: string;
}

export interface Session {
    access_token: string;
    token_type: string;
    expires_in: number;
    expires_at: number;
    refresh_token: string;
}

function isSameUser(left: User, right: User): boolean {
    return (
        left.id === right.id &&
        left.email === right.email &&
        left.firstName === right.firstName &&
        left.lastName === right.lastName &&
        left.pronouns === right.pronouns &&
        left.phone === right.phone &&
        left.companyName === right.companyName
    );
}

interface UserState {
    user: User | null;
    session: Session | null;
    selectedFestival: Festival | null;
    isInitialized: boolean;
    setUser: (user: User) => void;
    setSession: (session: Session | null) => void;
    setSelectedFestival: (festival: Festival) => void;
    validateSession: () => Promise<boolean>;
    refreshSession: () => Promise<void>;
    initializeAuth: () => Promise<void>;
    login: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
    logout: () => void;
}

export const useUserStore = create<UserState>((set) => {
    const supabase = createClient();
    const savedSession = typeof window !== 'undefined' ? localStorage.getItem('session') : null;
    const savedUser = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
    const initialSession = savedSession ? JSON.parse(savedSession) : null;
    const initialUser = savedUser ? JSON.parse(savedUser) : null;

    const isSessionExpired = (session: Session | null) => {
        if (!session) return true;
        const now = Date.now();
        const expiresAt = session.expires_at * 1000;
        return expiresAt <= now;
    };

    // Check if initial session is expired and clear user data if so
    const shouldClearUserData = initialSession && isSessionExpired(initialSession);
    if (shouldClearUserData && typeof window !== 'undefined') {
        console.log('Session expired on initialization, clearing user data');
        localStorage.removeItem('session');
        localStorage.removeItem('user');
        localStorage.removeItem('sb-gjxtnaqabmqamgwwxfjy-auth-token');
    }

    return {
        user: shouldClearUserData ? null : initialUser,
        session: shouldClearUserData ? null : initialSession,
        selectedFestival: null,
        isInitialized: false,
        setUser: (user) => {
            if (user) {
                localStorage.setItem('user', JSON.stringify(user));
            } else {
                localStorage.removeItem('user');
            }
            set({ user });
        },
        setSession: (session: Session | null) => {
            if (session) {
                localStorage.setItem('session', JSON.stringify(session));
            } else {
                localStorage.removeItem('session');
            }
            set({ session });
        },
        setSelectedFestival: (festival) => set({ selectedFestival: festival }),
        validateSession: async () => {
            let { session, user } = useUserStore.getState();
            if (!session || !user) {
                return false;
            }

            if (isSessionExpired(session)) {
                await useUserStore.getState().refreshSession();
                ({ session, user } = useUserStore.getState());
            }

            if (!session || !user || isSessionExpired(session)) {
                return false;
            }

            const {
                data: { user: authenticatedUser },
                error,
            } = await supabase.auth.getUser();

            if (error || !authenticatedUser || authenticatedUser.id !== user.id) {
                return false;
            }

            const { data: profileData, error: profileDataError } = await supabase
                .from('profiles')
                .select('*')
                .eq('user_id', authenticatedUser.id)
                .single();

            if (profileDataError) {
                console.error(
                    'Error fetching profile data during session validation',
                    profileDataError,
                );
                return true;
            }

            const verifiedUser: User = {
                id: authenticatedUser.id,
                email: authenticatedUser.email || '',
                firstName: profileData?.first_name || '',
                lastName: profileData?.last_name || '',
                pronouns: profileData?.pronouns || '',
                phone: profileData?.phone_number || '',
                companyName: profileData?.company_name || '',
            };

            const currentUser = useUserStore.getState().user;
            if (!currentUser || !isSameUser(currentUser, verifiedUser)) {
                useUserStore.getState().setUser(verifiedUser);
            }

            return true;
        },
        refreshSession: async () => {
            const { session } = useUserStore.getState();
            if (!session || !session.refresh_token) {
                useUserStore.getState().logout();
                return;
            }

            try {
                const { data, error } = await supabase.auth.refreshSession({
                    refresh_token: session.refresh_token,
                });

                if (error || !data.session) {
                    console.error('Failed to refresh session', error);
                    useUserStore.getState().logout();
                    return;
                }

                const newSession: Session = {
                    access_token: data.session.access_token,
                    token_type: data.session.token_type,
                    expires_in: data.session.expires_in,
                    expires_at:
                        data.session.expires_at || Date.now() + data.session.expires_in * 1000,
                    refresh_token: data.session.refresh_token,
                };
                useUserStore.getState().setSession(newSession);
            } catch (error) {
                console.error('Error refreshing session', error);
                useUserStore.getState().logout();
            }
        },
        initializeAuth: async () => {
            try {
                const { user, session } = useUserStore.getState();

                // If no user or session, mark as initialized
                if (!user || !session) {
                    console.log('No user or session found, marking as initialized');
                    set({ isInitialized: true });
                    return;
                }

                const isValid = await useUserStore.getState().validateSession();
                if (!isValid) {
                    useUserStore.getState().logout();
                }

                set({ isInitialized: true });
            } catch (error) {
                console.error('Error during auth initialization:', error);
                useUserStore.getState().logout();
                set({ isInitialized: true });
            }
        },
        login: async (email, password) => {
            try {
                const {
                    data: { session, user },
                    error,
                } = await supabase.auth.signInWithPassword({ email, password });

                if (error) {
                    console.error('Login error:', error);

                    // Check if the error is due to unverified email
                    if (
                        error.code === 'email_not_confirmed' ||
                        error.message?.includes('email not confirmed')
                    ) {
                        return {
                            success: false,
                            message: 'emailNotVerified',
                        };
                    }

                    return {
                        success: false,
                        message: 'Invalid email or password.',
                    };
                }

                if (!session) {
                    return {
                        success: false,
                        message: 'Failed to obtain session.',
                    };
                }

                if (!user) {
                    return {
                        success: false,
                        message: 'Failed to obtain user.',
                    };
                }

                useUserStore.getState().setSession({
                    access_token: session.access_token,
                    token_type: session.token_type,
                    expires_in: session.expires_in,
                    expires_at: session.expires_at || Date.now() + session.expires_in * 1000,
                    refresh_token: session.refresh_token,
                });

                const { data: profileData, error: profileDataError } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('user_id', user.id)
                    .single();

                if (profileDataError) {
                    console.error('Error fetching profile data', profileDataError);
                }

                const userWithRole: User = {
                    id: user.id,
                    email: user.email || '',
                    firstName: profileData?.first_name || '',
                    lastName: profileData?.last_name || '',
                    pronouns: profileData?.pronouns || '',
                    phone: profileData?.phone_number || '',
                    companyName: profileData?.company_name || '',
                };
                useUserStore.getState().setUser(userWithRole);

                return { success: true, message: 'Login successful.' };
            } catch (error) {
                console.error('Unexpected error during login:', error);
                return {
                    success: false,
                    message: 'An unexpected error occurred. Please try again.',
                };
            }
        },
        logout: () => {
            void supabase.auth.signOut();
            set({ user: null, session: null, selectedFestival: null });
            localStorage.removeItem('session');
            localStorage.removeItem('user');
            localStorage.removeItem('sb-gjxtnaqabmqamgwwxfjy-auth-token');
        },
    };
});
